<template>
  <span>
    <div v-if="showWorkerTypeOptions">
      <!-- <p class="body-1 font-weight-bold">{{ serviceOptionsLabel }}</p> -->

      <v-select
        @change="handleWorkerTypeOptionSelection"
        v-model="selectedWorkerType"
        :key="`worker-type-select-${workerTypeOptions}`"
        :items="workerTypeOptions"
        :label="responsibleTeamLabel"
        :rules="rules"
        item-value="value"
        item-text="text"
        outlined
      />
    </div>

    <div class="autocomplete">
      <v-autocomplete
        v-if="showProfessionalSelect"
        v-model="selectedProfessional"
        @change="handleProfessionalSelection"
        :rules="rules"
        :search-input.sync="professionalSearch"
        :label="selectProfessionalLabel"
        :no-data-text="$t('table.noResults')"
        :items="sortedRealEstateUsers"
        :loading="isSearchingCustomerUsers"
        :hint="typeToFilterProfessionalsLabel"
        :persistent-hint="true"
        prepend-inner-icon="mdi-magnify"
        item-text="name"
        item-value="id"
        return-object
        no-filter
        hide-no-data
        clearable
        outlined
      >
        <template v-slot:item="{ item }">
          <div class="worker-option">
            <div
              v-if="groupHeaderFor(item)"
              class="worker-group-header"
              :class="{
                'worker-group-header--selected': groupHeaderFor(item).isSelectedBranch,
                'worker-group-header--spaced': !groupHeaderFor(item).isFirstGroup,
              }"
            >
              {{ groupHeaderFor(item).label }}
              <template v-if="groupHeaderFor(item).isSelectedBranch">
                &middot; {{ branchSelectedTagLabel }}
              </template>
            </div>

            <div
              class="worker-item"
              :class="{ 'worker-item--selected-branch': isFromSelectedBranch(item) }"
            >
              <p class="body-2 mb-0">{{ item.name }}</p>
              <p class="caption grey--text mb-0">{{ item.username }}</p>
            </div>
          </div>
        </template>
      </v-autocomplete>
      <v-progress-circular
        v-show="isSearchingCustomerUsers"
        class="autocomplete-loading"
        color="primary"
        width="1.5"
        size="13"
        indeterminate
      />
    </div>
  </span>
</template>

<script>
import { mapGetters, mapMutations, mapActions } from 'vuex'

import { workerTypeOptions } from '@/util/enums'

const WORKER_TYPE_HOST = 'HOST'
const WORKER_TYPE_INTERNAL = 'INTERNAL'
const WORKER_TYPE_THIRD_PARTY = 'THIRD_PARTY'
const NO_BRANCH_KEY = '__no_branch__'

export default {
  name: 'workerTypeOptions',

  props: {
    rules: {
      type: Array,
    },
  },

  data() {
    return {
      showWorkerTypeOptions: false,
      showProfessionalSelect: false,
      selectedProfessional: null,
      professionalSearch: null,
      isSearchingCustomerUsers: false,
      timer: null,
    }
  },

  computed: {
    ...mapGetters(['appointmentFields', 'currentRealEstate', 'realEstateUsers']),

    serviceOptionsLabel() {
      return this.$t('operationTypes.serviceOptions')
    },

    selectProfessionalLabel() {
      return this.$t('operationTypes.selectProfessional')
    },

    responsibleTeamLabel() {
      return this.$t('operationTypes.responsibleTeam')
    },

    typeToFilterProfessionalsLabel() {
      return this.$t('operationTypes.hints.typeToFilterProfessionals')
    },

    selectedWorkerType: {
      get() {
        return this.appointmentFields.worker_type
      },
      set(value) {
        this.setAppointmentFields({ worker_type: value })
        return value
      },
    },

    workerTypeOptions() {
      return workerTypeOptions
    },

    branchSelectedTagLabel() {
      return this.$t('operationTypes.branchSelectedTag')
    },

    withoutBranchLabel() {
      return this.$t('operationTypes.withoutBranch')
    },

    selectedBranchId() {
      const branchId = this.appointmentFields.real_estate_branch_id

      if (branchId === null || branchId === undefined || branchId === '') {
        return null
      }

      return String(branchId)
    },

    userGroups() {
      const groups = new Map()

      this.realEstateUsers.forEach((user) => {
        const branchId = user.branch?.id
        const key = branchId !== null && branchId !== undefined
          ? String(branchId)
          : NO_BRANCH_KEY

        if (!groups.has(key)) {
          groups.set(key, {
            key,
            label: user.branch?.name || this.withoutBranchLabel,
            isSelectedBranch: this.isFromSelectedBranch(user),
            users: [],
          })
        }

        groups.get(key).users.push(user)
      })

      const sortedGroups = [...groups.values()].sort((groupA, groupB) => {
        if (groupA.isSelectedBranch !== groupB.isSelectedBranch) {
          return groupA.isSelectedBranch ? -1 : 1
        }

        if (groupA.key === NO_BRANCH_KEY || groupB.key === NO_BRANCH_KEY) {
          return groupA.key === NO_BRANCH_KEY ? 1 : -1
        }

        return groupA.label.localeCompare(groupB.label, 'pt-BR')
      })

      sortedGroups.forEach((group) => {
        group.users.sort((userA, userB) => {
          return (userA.name || '').localeCompare(userB.name || '', 'pt-BR')
        })
      })

      return sortedGroups
    },

    sortedRealEstateUsers() {
      return this.userGroups.reduce((users, group) => users.concat(group.users), [])
    },

    groupHeaders() {
      const headers = {}

      this.userGroups.forEach((group, index) => {
        const firstUser = group.users[0]

        if (!firstUser) {
          return
        }

        headers[firstUser.id] = {
          label: group.label,
          isSelectedBranch: group.isSelectedBranch,
          isFirstGroup: index === 0,
        }
      })

      return headers
    },
  },

  methods: {
    ...mapActions(['fetchRealEstateUsers']),
    ...mapMutations(['setAppointmentFields', 'setRealEstateUsers', 'setFilters']),

    isFromSelectedBranch(user) {
      if (!this.selectedBranchId || !user || !user.branch) {
        return false
      }

      return String(user.branch.id) === this.selectedBranchId
    },

    groupHeaderFor(user) {
      if (!user) {
        return null
      }

      return this.groupHeaders[user.id] || null
    },

    handleWorkerTypeOptionSelection(workerType) {
      clearTimeout(this.timer)
      this.setRealEstateUsers([])
      this.professionalSearch = null
      this.selectedProfessional = null
      this.handleProfessionalSelection(null)
      this.setAppointmentFields({ worker: null })
      this.setAppointmentFields({ worker_type: workerType })
    },

    handleProfessionalSelection(worker) {
      if (!worker) {
        this.selectedProfessional = null
        this.setAppointmentFields({ inspector_id: null })
        return
      }

      this.selectedProfessional = worker
      this.setAppointmentFields({ worker: this.selectedProfessional  })
      this.setAppointmentFields({ inspector_id: worker.id })
      this.$emit('professional-selected', worker)
    },

    updateWorkerTypeOptions(realEstate) {
      if (!realEstate || !realEstate.worker_types) {
        this.showWorkerTypeOptions = false
        return
      }

      const { worker_types } = realEstate
      const hasHost = worker_types.includes(WORKER_TYPE_HOST)
      const hasInternal = worker_types.includes(WORKER_TYPE_INTERNAL)

      if (hasHost && hasInternal) {
        this.showWorkerTypeOptions = true

        if (!this.appointmentFields.worker_type) {
          this.setAppointmentFields({ worker_type: WORKER_TYPE_HOST })
        }
      } else {
        this.showWorkerTypeOptions = false

        if (hasHost) {
          this.setAppointmentFields({ worker_type: WORKER_TYPE_HOST })
        } else if (hasInternal) {
          this.showProfessionalSelect = true
          this.setAppointmentFields({ worker_type: WORKER_TYPE_INTERNAL })
        } else {
          this.setAppointmentFields({ worker_type: WORKER_TYPE_THIRD_PARTY })
        }
      }
    },

    searchProfessionals(term) {
      clearTimeout(this.timer)
      this.isSearchingCustomerUsers = true

      const debounceDelay = term ? 500 : 0

      this.timer = setTimeout(() => {
        if (!this.showProfessionalSelect) {
          this.isSearchingCustomerUsers = false
          return
        }

        const filters = { userType: 'ROLE_INSPECTOR' }

        if (term) {
          filters.search = term
        }

        this.setFilters(filters)

        this.$nextTick(() => {
          this.fetchRealEstateUsers()
            .finally(() => {
              this.isSearchingCustomerUsers = false
            })
        })
      }, debounceDelay)
    },

    clearProfessional() {
      clearTimeout(this.timer)
      this.selectedProfessional = null
      this.professionalSearch = null
      this.setRealEstateUsers([])
      this.isSearchingCustomerUsers = false
      this.setAppointmentFields({ inspector_id: null })
    },

    restoreWorkerTypeIfNeeded() {
      if (!this.currentRealEstate || !this.currentRealEstate.worker_types) {
        return
      }

      const { worker_types } = this.currentRealEstate
      const hasHost = worker_types.includes(WORKER_TYPE_HOST)
      const hasInternal = worker_types.includes(WORKER_TYPE_INTERNAL)

      if (this.showWorkerTypeOptions) {
        this.setAppointmentFields({ worker_type: WORKER_TYPE_HOST })
      } else if (!hasHost && !hasInternal) {
        this.setAppointmentFields({ worker_type: WORKER_TYPE_THIRD_PARTY })
      } else if (hasHost) {
        this.setAppointmentFields({ worker_type: WORKER_TYPE_HOST })
      } else if (hasInternal) {
        this.setAppointmentFields({ worker_type: WORKER_TYPE_INTERNAL })
      }
    },
  },

  watch: {
    'appointmentFields.type': {
      handler(val) {
        if (!val) {
          this.restoreWorkerTypeIfNeeded()
        }
      },

      immediate: true,
    },

    'appointmentFields.worker_type': {
      handler(val) {
        if (!val) {
          this.restoreWorkerTypeIfNeeded()
        }
      },

      immediate: true,
    },

    currentRealEstate: {
      handler(realEstate) {
        this.updateWorkerTypeOptions(realEstate)
      },

      immediate: true,
    },

    selectedWorkerType(workerType) {
      if (workerType === WORKER_TYPE_INTERNAL) {
        this.showProfessionalSelect = true
        return
      }

      this.showProfessionalSelect = false
    },

    professionalSearch(term) {
      const search = term || ''

      if (this.selectedProfessional && search === this.selectedProfessional.name) {
        return
      }

      this.searchProfessionals(search)
    },

    showProfessionalSelect: {
      handler(isVisible) {
        if (!isVisible) {
          clearTimeout(this.timer)
          this.isSearchingCustomerUsers = false
          this.setRealEstateUsers([])
          return
        }

        if (!this.realEstateUsers.length) {
          this.searchProfessionals('')
        }
      },

      immediate: true,
    },
  },
}
</script>

<style scoped>
  .autocomplete {
    position: relative;
  }

  .autocomplete-loading {
    position: absolute;
    top: 28px;
    right: 73px;
    transform: translateY(-50%);
  }

  .worker-option {
    flex: 1 1 auto;
    margin: 0 -16px;
  }

  .worker-group-header {
    padding: 6px 16px;
    border-left: 3px solid transparent;
    font-size: 0.75rem;
    font-weight: 700;
    line-height: 1.3;
    color: rgba(0, 0, 0, 0.54);
  }

  .worker-group-header--spaced {
    margin-top: 12px;
  }

  .worker-group-header--selected {
    color: rgba(0, 0, 0, 0.78);
    border-left-color: #009DD9;
    background-color: rgba(0, 157, 217, 0.08);
  }

  .worker-item {
    padding: 8px 16px;
    border-left: 3px solid transparent;
  }

  .worker-item--selected-branch {
    border-left-color: #009DD9;
    background-color: rgba(0, 157, 217, 0.08);
  }
  </style>
